import {
    CleRepartitionModelDto,
    MutationGetListeClesRepartitionModelArgs,
    MutationGetListePlanAnalytiqueGeneriqueArgs,
    MutationGetListeSectionAnalytiqueGeneriqueArgs,
    PlanAnalytiqueModelDto,
    SectionAnalytiqueModelDto,
    TypeCleRepartitionEnum
} from 'src/graphql/generated-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { forkJoin, Observable, throwError } from 'rxjs';
import gql from 'graphql-tag';
import { catchError, map, take, tap } from 'rxjs/operators';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import {
    AnalyticalPlanInitAnalyticalParametersAction,
    InitAnalyticalDistributionAction,
    ResetAnalyticalPlanStateAction
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.actions';
import { Injectable } from '@angular/core';
import { GraphqlService } from 'src/app/_core/services/graphql.service';


interface SectionNode {
    section: SectionAnalytiqueModelDto;
    children: SectionNode[];
}

export interface AnalyticalPlanAndSections {
    plan: PlanAnalytiqueModelDto;
    sections: SectionAnalytiqueModelDto[];
    isArchived?: boolean;
}


/**
 * Ecrase un arbre de sectionNode pour récuperer les sections seulement
 */
const flattenSectionNodes = (nodes: SectionNode[]): SectionAnalytiqueModelDto[] => {
    return nodes.map(n => [n.section, ...flattenSectionNodes(n.children)]).flat();
};


/**
 * fonction permettant de trier 2 noeud en fonction de leur libelle de Section
 */
const sectionNodeSort = (a: SectionNode, b: SectionNode): number => {
    return a.section.Libelle.localeCompare(b.section.Libelle);
};

export class AnalyticalPlanStateModel {
    accountId: number = null;

    loading = false;

    overpaymentAmount: number = null;

    analyticalPlansAndSections: AnalyticalPlanAndSections[] = [];
}

@Injectable()
@State<AnalyticalPlanStateModel>({
    name: 'analyticalPlanState',
    defaults: new AnalyticalPlanStateModel()
})
export class AnalyticalPlanState {

    constructor(private graphqlService: GraphqlService) {
    }

    @Selector()
    public static overpaymentAmount(state: AnalyticalPlanStateModel): number {
        return state.overpaymentAmount;
    }

    @Selector()
    public static analyticalPlansAndSections(state: AnalyticalPlanStateModel): AnalyticalPlanAndSections[] {
        return state.analyticalPlansAndSections;
    }

    @Selector()
    public static loading(state: AnalyticalPlanStateModel): boolean {
        return state.loading;
    }

    @Action(InitAnalyticalDistributionAction)
    initAnalyticalDistribution({getState, patchState}: StateContext<AnalyticalPlanStateModel>,
                               {
                                   data: {
                                       accountId,
                                       analyticalSectionId,
                                       overpaymentAmount
                                   }
                               }: InitAnalyticalDistributionAction)
                                : Observable<{plans: PlanAnalytiqueModelDto[], sections: SectionAnalytiqueModelDto[], distributionKeys: CleRepartitionModelDto[]}> {
        patchState({
            loading: true,
            overpaymentAmount,
            accountId
        });

        return forkJoin({
            plans: this.getAnalyticalPlan(),
            sections: this.getGenericAnalyticalSectionList(),
            distributionKeys: this.getDistributionKeyList(accountId)
        }).pipe(
            take(1),
            tap(({plans, sections, distributionKeys}) => {

                if (!!analyticalSectionId) {
                    distributionKeys.forEach(dKey => {
                        if (dKey.IdSectionAnalytique === null) {
                            dKey.IdSectionAnalytique = analyticalSectionId;
                        }
                    });
                }

                const analyticalPlansAndSections = plans.map(plan => {
                    return {plan, sections: null};
                });

                analyticalPlansAndSections.forEach(p => {
                    this.SortedSectionOfPlan(p, sections);
                    const planDistributionKeys = distributionKeys.filter(dKey => dKey.IdPlanAnalytique === p.plan.Id);

                    if (!arrayNotEmpty(planDistributionKeys)) {
                        planDistributionKeys.push({
                            IdPlanAnalytique: p.plan.Id,
                            Pourcentage: 1,
                            IdSectionAnalytique: analyticalSectionId
                        });
                    }

                    p.plan.ListeClesRepartition = {
                        CleRepartitionModelDTO: planDistributionKeys.length === 1 ? planDistributionKeys
                            : planDistributionKeys.filter(dKey => !!dKey.IdSectionAnalytique)
                    };
                });

                patchState({
                    loading: false,
                    analyticalPlansAndSections
                });

            }),
            catchError(err => {
                patchState({
                    loading: false
                });
                return throwError(err);
            })
        );
    }

    @Action(AnalyticalPlanInitAnalyticalParametersAction)
    initSections({patchState}: StateContext<AnalyticalPlanStateModel>):  Observable<{plans: PlanAnalytiqueModelDto[], sections: SectionAnalytiqueModelDto[]}> {
        return forkJoin({
            plans: this.getAnalyticalPlan(),
            sections: this.getGenericAnalyticalSectionList()
        }).pipe(
            tap(({plans, sections}) => {
                const analyticalPlansAndSections = plans.map(plan => {
                    const sortSectionsList = (): SectionAnalytiqueModelDto[] => {
                        const findChildrenList = (parentId: number): SectionAnalytiqueModelDto[] => {
                            const directChildrenList = sections.filter(s =>
                                s.IdSectionAnalytiquePere === parentId
                                && s.IdPlanAnalytique === plan.Id
                            );

                            if (arrayNotEmpty(directChildrenList)) {
                                return directChildrenList.flatMap(child => [
                                        child,
                                        ...findChildrenList(child.Id) || []
                                    ]
                                );
                            }

                            return null;
                        };

                        return findChildrenList(null);
                    };

                    return {
                        plan,
                        sections: sortSectionsList()
                    };
                });

                patchState({
                    analyticalPlansAndSections
                });
            })
        );
    }

    @Action(ResetAnalyticalPlanStateAction)
    resetState({patchState}: StateContext<AnalyticalPlanStateModel>): void {
        patchState(new AnalyticalPlanStateModel());
    }

    private getAnalyticalPlan(): Observable<PlanAnalytiqueModelDto[]> {
        return this.graphqlService.mutateOne<'GetListePlanAnalytiqueGenerique', MutationGetListePlanAnalytiqueGeneriqueArgs>({
            mutation: gql`
                mutation GetListePlanAnalytiqueGenerique($contexte: ContextInfosInput, $criteres: CriteresPlanAnalytiqueDTOInput) {
                    GetListePlanAnalytiqueGenerique(contexte: $contexte, criteres: $criteres) {
                        PlanAnalytiqueModelDTO {
                            Id
                            Description

                            Libelle
                            ListeClesRepartition {
                                CleRepartitionModelDTO {
                                    IdCompte
                                    IdMouvement
                                    IdPlanAnalytique
                                    IdSectionAnalytique
                                    IsInitialSectionAnalytique
                                    IsSelected
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {IsClos: false}
            }
        }).pipe(
            map(GetListePlanAnalytiqueGenerique => GetListePlanAnalytiqueGenerique?.PlanAnalytiqueModelDTO || [])
        );
    }

    private getGenericAnalyticalSectionList(): Observable<SectionAnalytiqueModelDto[]> {
        return this.graphqlService.mutateOne<'GetListeSectionAnalytiqueGenerique', MutationGetListeSectionAnalytiqueGeneriqueArgs>({
            mutation: gql`
                mutation GetListeSectionAnalytiqueGenerique($contexte: ContextInfosInput, $criteres: CriteresSectionAnalytiqueDTOInput) {
                    GetListeSectionAnalytiqueGenerique(contexte: $contexte, criteres: $criteres) {
                        SectionAnalytiqueModelDTO {
                            Code
                            Description
                            Id
                            IdPlanAnalytique
                            IdSectionAnalytiquePere
                            IsAffichCompte
                            Libelle
                            LibellePosteBDG
                            Niveau
                            LibelleSectionPereFille
                            Pourcentage
                        }
                    }
                }
            `,
            variables: {
                criteres: {IsClos: false}
            }
        }).pipe(
            take(1),
            map(GetListeSectionAnalytiqueGenerique => GetListeSectionAnalytiqueGenerique?.SectionAnalytiqueModelDTO || [])
        );
    }

    private getDistributionKeyList(accountId: number): Observable<CleRepartitionModelDto[]> {
        return this.graphqlService.mutateOne<'GetListeClesRepartitionModel', MutationGetListeClesRepartitionModelArgs>({
            mutation: gql`
                mutation GetListeClesRepartitionModel($contexte: ContextInfosInput,
                    $criteres: CriteresCompteSectionAnalytiqueDTOInput) {
                    GetListeClesRepartitionModel(contexte: $contexte, criteres: $criteres) {
                        CleRepartitionModelDTO {
                            IdMouvement
                            IdPlanAnalytique
                            IdSectionAnalytique
                            IsInitialSectionAnalytique
                            LibellePlanAnalytique
                            LibelleSectionAnalytique
                            Montant
                            Pourcentage
                            NumeroCompte
                            MontantConsultation
                            MontantARepartir
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    CompteId: accountId,
                    VisionCle: TypeCleRepartitionEnum.Toutes
                }
            }
        }).pipe(
            map(GetListeClesRepartitionModel => GetListeClesRepartitionModel?.CleRepartitionModelDTO || [])
        );
    }

    /**
     * Rattache les section au plan concerné en les triant pour l'affichage
     */
    private SortedSectionOfPlan(planAndSections: AnalyticalPlanAndSections, sections: SectionAnalytiqueModelDto[]): void {
        // On sélectionne les sections concernée par le plan
        const concernedSections = sections.filter(s => s.IdPlanAnalytique === planAndSections.plan.Id);

        // fonction qui recrée l'arbre de section récursivement
        const childrenTree = (sectionsNode: SectionNode[]) => {

            // On recherche les sections filles et on les transforme en noeud
            sectionsNode.forEach(node => {
                node.children = concernedSections.filter(s =>
                    s.IdSectionAnalytiquePere === node.section.Id && s.IdPlanAnalytique === planAndSections.plan.Id
                ).map(s => {
                    return {section: s, children: []};
                });

                // Si une section a des filles on effectue le traitement récursivement
                if (arrayNotEmpty(node.children)) {
                    // On tri les noeud filles
                    node.children.sort(sectionNodeSort);

                    // récursion
                    childrenTree(node.children);
                }
            });
        };

        // Récupération des sections du 1er niveau
        const rootSections = concernedSections.filter(s => s.Niveau === 1).map(s => {
            return {section: s, children: []};
        });
        // tri
        rootSections.sort(sectionNodeSort);
        // début de la construction récursive
        childrenTree(rootSections);

        // On récupère la liste des section écrasée
        planAndSections.sections = flattenSectionNodes(rootSections);
    }

}
